/**
 * bindPendingInvite — binds a stored InviteIQ code and/or surface katalog to a user on signup/login.
 * Calls the bind_invite_on_signup RPC, then clears localStorage on success.
 *
 * ALWAYS calls the RPC — even with no invite/surface — to ensure
 * the customer record exists with platform fallback attribution.
 *
 * Priority:
 *   1. Explicit ?iq= token (strongest signal)
 *   2. First-touch surface katalog ID (creator route landing)
 *   3. Platform fallback (Kloutforce Information)
 */
import type { SupabaseClient } from '@supabase/supabase-js';
import { getPendingInviteIQ, clearPendingInviteIQ, getSurfaceKatalogId, clearSurfaceKatalogId } from './storage';

export interface BindResult {
  status: 'bound' | 'already_bound' | 'already_locked' | 'inactive' | 'not_found' | 'no_pending' | 'surface_bound' | 'platform_fallback' | 'error';
  /** How the attribution was resolved: invite | surface | platform | existing */
  resolvedVia?: string;
  /** Why fallback was applied: no_inviter | no_agent | no_country_ga */
  fallbackReason?: string | null;
  /** The inviter's user ID (null if platform fallback) */
  inviterUserId?: string | null;
  inviteCode?: string;
  surfaceKatalogId?: string;
}

export async function bindPendingInvite(
  supabase: SupabaseClient,
  userId: string,
  /** Override surface katalog from React ref (legacy compat) */
  surfaceKatalogIdOverride?: string | null,
): Promise<BindResult> {
  const code = getPendingInviteIQ();
  const surfaceKatalogId = surfaceKatalogIdOverride || getSurfaceKatalogId();

  // ALWAYS call RPC — even with no invite/surface, we ensure
  // the customer record exists with platform fallback attribution.

  try {
    const { data, error } = await supabase.rpc('bind_invite_on_signup', {
      p_invite_code: code || '',
      p_user_id: userId,
      p_surface_katalog_id: surfaceKatalogId || null,
    });

    if (error) {
      console.warn('[InviteIQ] bind RPC error:', error.message);
      if (error.message?.includes('inactive')) {
        return { status: 'inactive', inviteCode: code || undefined };
      }
      return { status: 'error', inviteCode: code || undefined };
    }

    const rpcStatus = data?.status as string;
    const resolvedVia = data?.resolved_via as string | undefined;
    const fallbackReason = data?.fallback_reason as string | null | undefined;
    const inviterUserId = data?.inviter_user_id as string | null | undefined;

    console.log('[InviteIQ] Bind result:', {
      rpcStatus,
      resolvedVia,
      fallbackReason,
      inviterUserId,
      code: code ? '***' : null,
      surfaceKatalogId,
    });

    // Clear stored values on successful bind
    if (rpcStatus === 'bound' || rpcStatus === 'locked_organic' || rpcStatus === 'already_locked' || rpcStatus === 'invalid_code_locked_organic') {
      clearPendingInviteIQ();
      clearSurfaceKatalogId();
    }

    if (rpcStatus === 'inactive') {
      return { status: 'inactive', resolvedVia: 'invite', inviteCode: code || undefined };
    }

    if (rpcStatus === 'already_locked') {
      return {
        status: 'already_locked',
        resolvedVia: resolvedVia || 'existing',
        fallbackReason: null,
        inviteCode: code || undefined,
        surfaceKatalogId: surfaceKatalogId || undefined,
      };
    }

    // Map RPC resolved_via to client status
    let resultStatus: BindResult['status'];
    if (resolvedVia === 'invite') {
      resultStatus = 'bound';
    } else if (resolvedVia === 'surface') {
      resultStatus = 'surface_bound';
    } else if (resolvedVia === 'platform') {
      resultStatus = 'platform_fallback';
    } else if (code) {
      resultStatus = 'bound';
    } else if (surfaceKatalogId) {
      resultStatus = 'surface_bound';
    } else {
      resultStatus = 'platform_fallback';
    }

    return {
      status: resultStatus,
      resolvedVia: resolvedVia || (code ? 'invite' : surfaceKatalogId ? 'surface' : 'platform'),
      fallbackReason: fallbackReason || null,
      inviterUserId: inviterUserId || null,
      inviteCode: code || undefined,
      surfaceKatalogId: surfaceKatalogId || undefined,
    };
  } catch (err) {
    console.warn('[InviteIQ] bind error:', err);
    return { status: 'error', inviteCode: code || undefined };
  }
}
